import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://metaconnect.fr";
const SITE_NAME = "MetaConnect";
const DEFAULT_OG = `${SITE_URL}/og-image.jpg`;

/**
 * Reusable SEO head component.
 * Pass per-page title / description / canonical / og overrides.
 */
export default function SEO({
  title,
  exactTitle = false,
  description,
  canonical = "/",
  image = DEFAULT_OG,
  type = "website",
  noIndex = false,
  schema, // optional JSON-LD schema (object or array)
}) {
  React.useEffect(() => {
    document.getElementById("canonical-bootstrap")?.remove();
  }, []);

  const fullTitle = exactTitle || title.includes(SITE_NAME) ? title : `${title} · ${SITE_NAME}`;
  const url = canonical.startsWith("http") ? canonical : `${SITE_URL}${canonical}`;
  const ogImage = image.startsWith("http") ? image : `${SITE_URL}${image}`;

  const schemaArray = schema
    ? Array.isArray(schema) ? schema : [schema]
    : [];

  // Always inject the Organization schema as the first JSON-LD block on every page,
  // unless the caller already passed it (avoid duplication by @id).
  const orgId = ORG_SCHEMA["@id"];
  const callerHasOrg = schemaArray.some((s) => s && s["@id"] === orgId);
  const finalSchemas = callerHasOrg ? schemaArray : [ORG_SCHEMA, ...schemaArray];

  return (
    <Helmet>
      <html lang="fr" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {!noIndex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="fr_FR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Theme color */}
      <meta name="theme-color" content="#0A1A33" />

      {/* JSON-LD */}
      {finalSchemas.map((s, i) => (
        <script key={`${s["@type"] || "schema"}-${i}`} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

/* ---------- Reusable schemas ---------- */
export const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  alternateName: "MetaConnect.fr",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}/icon-512.png`,
    width: 512,
    height: 512,
  },
  image: `${SITE_URL}/icon-512.png`,
  description:
    "MetaConnect est une plateforme française e-santé interopérable et conforme aux référentiels de l'Agence du Numérique en Santé (ANS). Elle accompagne professionnels, éditeurs et établissements dans l'intégration des services socles nationaux : INSi (Identité Nationale de Santé), DMP (Dossier Médical Partagé), MSSanté (Espace de Confiance), Pro Santé Connect (e-CPS / OAuth2), FHIR France. Hébergée HDS, conforme RGPD et ISO 27001.",
  slogan: "Connecter l'existant, accélérer la conformité, fluidifier le parcours.",
  email: "contact@metaconnect.fr",
  areaServed: { "@type": "Country", name: "France" },
  knowsAbout: [
    "MSSanté", "Espace de Confiance MSSanté", "Mon Espace Santé",
    "DMP", "Dossier Médical Partagé", "Certification CNDA",
    "INS", "INSi", "Identité Nationale de Santé",
    "Pro Santé Connect", "PSC", "e-CPS", "OAuth2 santé",
    "FHIR", "FHIR France", "HL7", "CDA",
    "Ségur du numérique en santé", "Référentiel ANS",
    "HDS", "ISO 27001", "RGPD santé",
    "Interopérabilité santé", "Télémédecine", "Téléconsultation", "Téléexpertise",
  ],
  contactPoint: [{
    "@type": "ContactPoint",
    contactType: "customer support",
    email: "contact@metaconnect.fr",
    availableLanguage: ["French"],
    areaServed: "FR",
  }],
  // sameAs = profils appartenant a MetaConnect uniquement (pas de partenaires ni d organismes officiels :
  // sameAs signifie "cette organisation EST ce profil"). Page LinkedIn MetaConnect.fr verifiee le 16/10/2026.
  sameAs: [
    "https://www.linkedin.com/company/135264913/",
  ],
};

export const MSSANTE_SERVICE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${SITE_URL}/mssante#service`,
  name: "Boîte aux lettres MSSanté MetaConnect",
  serviceType: "Messagerie sécurisée de santé MSSanté",
  provider: { "@id": `${SITE_URL}/#organization` },
  areaServed: { "@type": "Country", name: "France" },
  audience: {
    "@type": "Audience",
    audienceType: "Professionnels de santé (médecins, IDEL, opticiens, audioprothésistes)",
  },
  description:
    "Création et hébergement de boîtes aux lettres MSSanté conformes ANS, accessibles via Pro Santé Connect, avec annuaire national, archivage légal et hébergement HDS.",
  termsOfService: `${SITE_URL}/cgv`,
  offers: {
    "@type": "Offer",
    priceCurrency: "EUR",
    availability: "https://schema.org/InStock",
    url: `${SITE_URL}/mssante/inscription`,
  },
};

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://metaconnect.fr/#website",
  name: SITE_NAME,
  url: SITE_URL,
  inLanguage: "fr-FR",
  publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
};

export const breadcrumb = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: it.name,
    item: it.url.startsWith("http") ? it.url : `${SITE_URL}${it.url}`,
  })),
});

export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "MetaConnect Platform",
  applicationCategory: "HealthApplication",
  operatingSystem: "Web",
  url: `${SITE_URL}/produit`,
  description:
    "Plateforme e-santé modulaire : coordination de soins, télésanté, messagerie sécurisée MSSanté, alimentation DMP / Mon Espace Santé, identito-vigilance INSi, authentification Pro Santé Connect.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "EUR", description: "Démo gratuite" },
  publisher: { "@type": "Organization", name: SITE_NAME },
};

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((q) => ({
    "@type": "Question",
    name: q.q,
    acceptedAnswer: { "@type": "Answer", text: q.a },
  })),
});

/**
 * Schema.org ItemList des pages MSSanté par métier.
 * Aide Google à comprendre la structure thématique du site et favorise
 * l'affichage de sitelinks enrichis dans les SERP autour de la marque MetaConnect.
 */
export const MSSANTE_PERSONAS_ITEMLIST = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "@id": `${SITE_URL}/#personas-mssante`,
  name: "MSSanté par métier de santé",
  description:
    "Pages MSSanté MetaConnect dédiées aux différents métiers de santé : médecins, infirmiers libéraux, opticiens, audioprothésistes, ainsi que la souscription en ligne et les solutions assureurs.",
  numberOfItems: 7,
  itemListOrder: "https://schema.org/ItemListUnordered",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      url: `${SITE_URL}/mssante-medecin`,
      name: "MSSanté Médecin",
      description: "Boîte MSSanté pour médecins, cabinets, CPTS et MSP.",
    },
    {
      "@type": "ListItem",
      position: 2,
      url: `${SITE_URL}/mssante-infirmier-liberal`,
      name: "MSSanté Infirmier libéral",
      description: "IDEL en tournée, DMP mobile, coordination patients fragiles.",
    },
    {
      "@type": "ListItem",
      position: 3,
      url: `${SITE_URL}/mssante/opticien`,
      name: "MSSanté Opticien",
      description: "BAL organisationnelle pour magasins et centres de santé visuelle.",
    },
    {
      "@type": "ListItem",
      position: 4,
      url: `${SITE_URL}/mssante/audioprothesiste`,
      name: "MSSanté Audioprothésiste",
      description: "Échanges sécurisés ORL ↔ patient ↔ assurance.",
    },
    {
      "@type": "ListItem",
      position: 5,
      url: `${SITE_URL}/mssante/professionnels-de-sante`,
      name: "Boîte MSSanté partagée pour équipes et structures",
      description: "Boîte MSSanté organisationnelle multi-utilisateurs pour équipes et structures de santé.",
    },
    {
      "@type": "ListItem",
      position: 6,
      url: `${SITE_URL}/mssante/inscription`,
      name: "Souscrire une boîte MSSanté",
      description: "Souscription en ligne, mise en service sous 24-48 h.",
    },
    {
      "@type": "ListItem",
      position: 7,
      url: `${SITE_URL}/lutte-fraude-sante`,
      name: "Lutte contre la fraude documentaire en santé",
      description: "Détection documentaire pour assureurs et complémentaires santé.",
    },
  ],
};
